import AlternateEmail from "@mui/icons-material/AlternateEmail";
import Tag from "@mui/icons-material/Tag";
import {
  Box,
  FormControl,
  type FormControlProps,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  type SvgIconTypeMap,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import {
  type FC,
  type ReactElement,
  type ReactNode,
  createElement,
  useCallback,
  useEffect,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Check } from "../../../styles/icons/check";
import { Employees } from "../../../styles/icons/employees";
import { EmployeesConnection } from "../../../styles/icons/employees-connection";
import { EmployeesManager } from "../../../styles/icons/employees-manager";
import { EmployeesV2 } from "../../../styles/icons/employees-v2";
import { X } from "../../../styles/icons/x";
import type { ChannelName } from "../../channels/types";
import { Tile } from "../../generic/components/tile/tile";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { type ToTarget, useToTarget } from "../use-to-target";

const SelectTile: FC<{
  children: ReactNode;
  onClick: () => void;
  selected: boolean;
  disabled?: boolean;
  name: string;
  title: string;
  subtitle: string;
  examples: {
    negative?: boolean;
    text: ReactNode;
  }[];
}> = ({ children, title, subtitle, examples, ...rest }) => (
  <Tooltip
    placement="left-start"
    arrow
    describeChild
    slots={{
      transition: Zoom,
    }}
    slotProps={{
      popper: {
        modifiers: [
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
              padding: 16,
              altAxis: true,
              tether: false,
            },
          },
        ],
      },
      tooltip: {
        sx: {
          width: {
            xs: "20rem",
            sm: 360,
          },
          maxWidth: "calc(100vw - 2rem)",
          overflowWrap: "anywhere",
          backgroundColor: "background.default",
          borderColor: "neutral.400",
          borderWidth: 1,
          borderStyle: "solid",
        },
      },
      arrow: {
        sx: {
          color: "neutral.400",
        },
      },
    }}
    title={
      <Paper
        sx={{
          backgroundColor: "transparent",
          boxShadow: "none",
          padding: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "text.primary",
          }}
        >
          Examples
        </Typography>
        <List>
          {examples.map(({ negative, text }, i) => (
            <ListItem key={i}>
              <ListItemIcon>
                {negative ? (
                  <X
                    data-testid="XIcon"
                    sx={{
                      color: "error.main",
                    }}
                  />
                ) : (
                  <Check
                    data-testid="CheckIcon"
                    sx={{
                      color: "success.main",
                    }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={text}
                sx={{
                  color: "text.primary",
                }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    }
  >
    <Grid width="100%">
      <Tile
        sx={{
          height: 130,
          flexDirection: "row",
          justifyContent: "start",
          width: "100%",
        }}
        {...rest}
      >
        <Box width={130} minWidth={130} display="flex" justifyContent="center">
          {children}
        </Box>
        <Box flexDirection="column">
          <Typography variant="h6" sx={{ mt: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {subtitle}
          </Typography>
        </Box>
      </Tile>
    </Grid>
  </Tooltip>
);

export const ToVertical: FC<
  FormControlProps & {
    enableChannels?: boolean;
    setIcon?: (icon: ReactElement) => void;
    selectedChannel: ChannelName;
    isNewsletter?: boolean;
  }
> = ({ enableChannels, disabled, setIcon, selectedChannel, isNewsletter, ...props }) => {
  const { toTarget } = useToTarget();

  const { control, setValue, getValues } = useFormContext<MomentFormMoment>();

  useWatch({
    control,
    name: ["timezone"],
  });
  const [timezone] = getValues(["timezone"]);

  const targetting_channel = toTarget === "channel" || toTarget === "notify";

  useEffect(() => {
    // top level channel has changed so reset the form
    if (toTarget === "channel" && !enableChannels) {
      setValue("target_manager", false);
      setValue("target_connection_type", null);
      setValue("target_channel", false);
      setValue("only_additional_recipients", false);
      setValue("notify_channel", false);
    }
  }, [enableChannels, setValue, toTarget]);

  const handleChange = useCallback(
    (newTarget?: ToTarget["toTarget"]): void => {
      const targetChannel = newTarget === "channel";
      // There is a business decision to default to manager when connection is enabled
      // This also lets us avoid an interim state where type is connection but there is no connection itself
      const targetConnectionType = newTarget === "connection" ? "manager" : null;
      const onlyAdditionalRecipients = newTarget === "notify" || targetChannel;

      const noLongerTargetingChannel = targetting_channel && !targetChannel;
      if (noLongerTargetingChannel) {
        setValue("timezone", null);
        setValue("notify_channel", false);
      }

      setValue("target_manager", false);
      setValue("target_connection_type", targetConnectionType);
      setValue("target_channel", targetChannel);
      setValue("only_additional_recipients", onlyAdditionalRecipients);
      // If we're not targetting only_additional_recipients, reset the additional_recipients
      // as enqueuer targets only additional_recipients if any values are present
      if (!onlyAdditionalRecipients) setValue("additional_recipients", []);

      if (targetChannel && enableChannels) {
        setValue("segment", null);
        if (!timezone) setValue("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
      }
    },
    [enableChannels, setValue, targetting_channel, timezone],
  );

  useEffect(() => {
    setIcon?.(createElement(iconMap[toTarget], { sx: { height: "45px", width: "45px", mb: 0.5 } }));
  }, [setIcon, toTarget]);

  // This is so when a v1 moment which is currently send_to_manager is opened in the v2 editor
  // we can change it to send_to_connection with manager as the type
  // This will only apply in the frontend ui until the moment is saved
  // At which point the moment will now be send_to_connection typed
  useEffect(() => {
    // this can only be manager if this was a v1 moment. If it was a v2 moment it would be connection.
    if (toTarget === "manager") {
      // this will propagate to toTarget => connection
      handleChange("connection");
    }
  }, [toTarget, handleChange]);

  return (
    <FormControl
      variant="standard"
      sx={{ mb: 1, minWidth: 120, width: "100%" }}
      {...props}
      fullWidth
    >
      <Grid
        display="flex"
        flexDirection="column"
        alignItems="start"
        spacing={2}
        width="100%"
        container
      >
        <SelectTile
          disabled={disabled}
          name="to-employees"
          onClick={() => handleChange("employee")}
          selected={toTarget === "employee"}
          examples={[
            {
              text: <>Send a message to each person when it’s their birthday</>,
            },
            {
              text: <>Send a message to each person in the Marketing team</>,
            },
            {
              text: <>Send a message to each person who is a Manager</>,
            },
          ]}
          title="People in a segment"
          subtitle="Send a direct message to each person in a specified segment"
        >
          <EmployeesV2
            sx={{
              height: "45px",
              width: "45px",
              color: toTarget === "employee" ? "neutral.100" : "neutral.500",
            }}
          />
        </SelectTile>
        <SelectTile
          name="to-connection"
          onClick={() => handleChange("connection")}
          selected={toTarget === "connection"}
          disabled={disabled}
          examples={[
            {
              text: <>Send a message to a person’s manager when their birthday is coming up</>,
            },
            {
              text: (
                <>
                  Send a message to a person’s manager if they’ve been selected to partake in a
                  workshop
                </>
              ),
            },
            {
              text: <>Send a message to a person’s buddy before they start</>,
            },
            {
              negative: true,
              text: <>One off communications to managers</>,
            },
          ]}
          title="Connection of a Person"
          subtitle="Send a direct message to the connections of people in a segment"
        >
          <EmployeesConnection
            sx={{
              height: "65px",
              width: "65px",
              color: toTarget === "connection" ? "neutral.100" : "neutral.500",
            }}
          />
        </SelectTile>

        {!isNewsletter && selectedChannel !== "personal_email" && (
          <SelectTile
            name="to-specific"
            onClick={() => handleChange("notify")}
            selected={toTarget === "notify"}
            disabled={disabled}
            examples={[
              {
                text: <>Send a message to the Office Manager when an employee is due to start</>,
              },
              {
                text: (
                  <>
                    Send a message to a manager’s alias email when an person’s birthday is coming up
                    within their store/location
                  </>
                ),
              },
            ]}
            title="Specific person on Event"
            subtitle="Send a direct message to a specific person when an automated event happens for another person in a segment."
          >
            <AlternateEmail
              sx={{
                height: "45px",
                width: "45px",
                color: toTarget === "notify" ? "neutral.100" : "neutral.500",
              }}
            />
          </SelectTile>
        )}

        {enableChannels && (
          <SelectTile
            name="to-channel"
            onClick={() => handleChange("channel")}
            selected={toTarget === "channel"}
            disabled={disabled}
            examples={[
              {
                text: <>Send a message to #company-wide channel</>,
              },
              {
                text: <>Send a message to #sales-team channel</>,
              },
            ]}
            title="Channel in Slack / Teams"
            subtitle="Send a message in a Slack or Teams channel"
          >
            <Tag
              sx={{
                height: "45px",
                width: "45px",
                color: toTarget === "channel" ? "neutral.100" : "neutral.500",
              }}
            />
          </SelectTile>
        )}
      </Grid>
    </FormControl>
  );
};

const iconMap: Record<
  ToTarget["toTarget"],
  OverridableComponent<SvgIconTypeMap<unknown, "svg">> & { muiName: string }
> = {
  channel: Tag,
  notify: AlternateEmail,
  manager: EmployeesManager,
  connection: EmployeesConnection,
  employee: Employees,
};
